import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2 } from "lucide-react";
import { COMPANY, PRESENCE, SECTORS } from "@/data/company";
import BankingPartners from "@/components/BankingPartners";
const HERO_IMG =
  "https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative isolate min-h-[92vh] flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Modern institutional architecture"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 via-slate-950/20 to-slate-50" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pb-16 lg:pb-24 w-full">
          <div className="max-w-3xl">
            <img
              src={COMPANY.logo}
              alt="Finovest Holdings Ltd logo"
              data-testid="hero-logo"
              className="w-64 h-64 lg:w-80 lg:h-80 object-contain drop-shadow-2xl -ml-6 lg:-ml-10 -mb-4"
            />
            <h1
              data-testid="hero-title"
              className="mt-8 font-serif font-light text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05]"
              style={{ textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}
            >
              {COMPANY.name}
            </h1>
            <p
              data-testid="hero-tagline"
              className="mt-6 font-serif italic text-amber-200/95 text-xl lg:text-2xl tracking-tight leading-snug max-w-2xl"
              style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
            >
              {COMPANY.tagline}
            </p>
            <p
              className="mt-6 text-white/90 max-w-2xl text-base lg:text-lg leading-relaxed"
              style={{ textShadow: "0 1px 12px rgba(0,0,0,0.4)" }}
            >
              {COMPANY.description}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/portfolio"
                data-testid="hero-cta-portfolio"
                className="text-[12px] tracking-[0.2em] uppercase font-semibold bg-white text-slate-900 px-6 py-3.5 hover:bg-slate-100 transition-colors inline-flex items-center gap-2"
              >
                View Portfolio <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="text-[12px] tracking-[0.2em] uppercase font-semibold border border-white/60 text-white px-6 py-3.5 hover:bg-white hover:text-slate-900 transition-colors"
              >
                Contact the firm
              </Link>
            </div>
          </div>

          {/* Floating stat card */}
          <div
            data-testid="hero-stat-card"
            className="mt-12 lg:mt-20 grid grid-cols-1 sm:grid-cols-3 backdrop-blur-xl bg-white/85 border border-white/60 shadow-xl"
          >
            {[
              { k: "8+", v: "Countries of presence" },
              { k: "5", v: "Core investment sectors" },
              { k: "6", v: "Tier-one banking partners" },
            ].map((s, i) => (
              <div
                key={s.v}
                className={`p-6 lg:p-8 ${
                  i !== 2 ? "border-b sm:border-b-0 sm:border-r border-slate-200" : ""
                }`}
              >
                <div className="font-serif text-3xl lg:text-4xl text-slate-900">
                  {s.k}
                </div>
                <div className="overline mt-2 text-slate-500">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4 fade-up">
            <div className="overline">The Firm</div>
            <h2 className="mt-5 font-serif text-3xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
              A holdings group built for the long horizon.
            </h2>
          </div>
          <div className="lg:col-span-8 fade-up">
            <p className="text-slate-700 text-lg leading-relaxed">
              Finovest Holdings Ltd allocates patient capital across resilient
              sectors and high-quality operators. We act as principals — not
              intermediaries — and structure our investments to compound across
              market cycles.
            </p>
            <p className="mt-6 text-slate-500 leading-relaxed">
              From a BVI-registered platform with a Singapore operating hub, we
              originate and manage commitments across five core sectors —
              fintech, healthcare, agriculture, real estate, and clean energy
              — held through bespoke structures aligned with management teams
              and co-investors.
            </p>
            <Link
              to="/about"
              data-testid="about-cta-home"
              className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-semibold text-slate-900 border-b border-slate-900 pb-1 hover:gap-3 transition-all"
            >
              About the firm <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTORS BENTO */}
      <section
        data-testid="sectors-section"
        className="py-24 lg:py-32 bg-white border-y border-slate-200"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <div className="overline">Investment Sectors</div>
              <h2 className="mt-5 font-serif text-3xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
                Capital deployed where durability meets demand.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="sectors-view-all"
              className="text-[12px] tracking-[0.2em] uppercase font-semibold text-slate-900 hover:text-emerald-800 transition-colors inline-flex items-center gap-2"
            >
              Our approach <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            {SECTORS.map((s) => (
              <article
                key={s.key}
                data-testid={`sector-card-${s.key}`}
                className="group relative bg-white p-8 lg:p-10 min-h-[360px] flex flex-col justify-between hover:bg-slate-50 transition-colors"
              >
                {s.image ? (
                  <div className="absolute inset-0">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover opacity-15 group-hover:opacity-25 transition-opacity"
                    />
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-slate-950" />
                )}
                <div className="relative">
                  <s.Icon
                    size={28}
                    className={s.image ? "text-slate-900" : "text-emerald-400"}
                    strokeWidth={1.4}
                  />
                </div>
                <div className="relative mt-auto">
                  <div
                    className={`overline ${
                      s.image ? "text-slate-500" : "text-slate-400"
                    }`}
                  >
                    Sector 0{SECTORS.indexOf(s) + 1}
                  </div>
                  <h3
                    className={`mt-3 font-serif text-2xl lg:text-3xl tracking-tight ${
                      s.image ? "text-slate-900" : "text-white"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`mt-3 text-sm leading-relaxed ${
                      s.image ? "text-slate-600" : "text-slate-400"
                    }`}
                  >
                    {s.summary}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL PRESENCE */}
      <section
        data-testid="presence-section"
        className="py-24 lg:py-32 bg-slate-950 text-slate-100"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="overline text-slate-400">Global Presence</div>
              <h2 className="mt-5 font-serif text-3xl lg:text-5xl tracking-tight text-white leading-[1.05]">
                A footprint across four continents.
              </h2>
              <p className="mt-6 text-slate-400 leading-relaxed max-w-md">
                Operating teams, advisors, and portfolio assets across
                Asia-Pacific, EMEA, the Americas, and Africa.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-slate-300">
                <Globe2 size={18} />
                <span className="text-sm">{PRESENCE.length} active geographies</span>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
                {PRESENCE.map((p) => (
                  <div
                    key={p.country}
                    data-testid={`presence-${p.country.toLowerCase().replace(/\s+/g, "-")}`}
                    className="bg-slate-950 p-6 lg:p-7 hover:bg-slate-900 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-serif text-xl lg:text-2xl text-white">
                          {p.country}
                        </div>
                        <div className="mt-2 overline text-slate-500">
                          {p.note}
                        </div>
                      </div>
                      <span className="text-2xl">{p.flag}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <BankingPartners />

      {/* CLOSING CTA */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <div className="overline">Partner with Finovest</div>
          <h2 className="mt-6 font-serif text-3xl lg:text-5xl tracking-tight text-slate-900 leading-[1.1]">
            Long-term capital. Operator-led conviction.
            <br />
            Discreet, institutional execution.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="closing-cta-contact"
              className="text-[12px] tracking-[0.2em] uppercase font-semibold bg-slate-900 text-white px-7 py-4 hover:bg-slate-800 transition-colors"
            >
              Speak with us
            </Link>
            <Link
              to="/portfolio"
              data-testid="closing-cta-portfolio"
              className="text-[12px] tracking-[0.2em] uppercase font-semibold border border-slate-900 text-slate-900 px-7 py-4 hover:bg-slate-900 hover:text-white transition-colors"
            >
              See our portfolio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
